import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Copy, Download, ArrowLeft, LayoutDashboard, Settings as SettingsIcon, FileText, Mail, Send, X, Share2 } from 'lucide-react';
import { ambAxios, getAmbToken, clearAmbToken, C, apiError } from './ambApi';

export default function AmbassadorResources() {
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(null);
  const [em, setEm] = useState(null);
  const [share, setShare] = useState(null); // { type, email, note }
  const [sending, setSending] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [pdfCat, setPdfCat] = useState(null);

  const downloadPresentation = async (cat) => {
    setPdfCat(cat);
    try {
      const r = await ambAxios().get(`/ambassador/materials/pdf?category=${cat}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `easypassing-${cat}-presentation.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Presentation downloaded');
    } catch (e) { toast.error(apiError(e)); } finally { setPdfCat(null); }
  };

  useEffect(() => {
    if (!getAmbToken()) { navigate('/ambassador/login'); return; }
    ambAxios().get('/ambassador/me').then((r) => setA(r.data))
      .catch((e) => { if (e?.response?.status === 401) { clearAmbToken(); navigate('/ambassador/login'); } })
      .finally(() => setLoading(false));
    ambAxios().get('/ambassador/materials').then((r) => setMaterials(r.data.materials)).catch(() => {});
  }, [navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>;
  if (!a) return null;
  const link = a.referral_link || `https://easypassing.life/ambassador/${a.referral_code}`;
  const name = `${a.first_name} ${a.last_name}`.trim();
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  const shareLink = async () => {
    const text = `Planning ahead is one of the most loving gifts you can give your family. Start free with EasyPassing:`;
    if (navigator.share) {
      try { await navigator.share({ title: 'EasyPassing', text, url: link }); } catch (e) { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(link); toast.success('Referral link copied to clipboard'); }
      catch (e) { toast.error('Could not copy link'); }
    }
  };

  const downloadFlyer = async (type) => {
    setDl(type);
    try {
      const r = await ambAxios().get(`/ambassador/flyer/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link_el = document.createElement('a');
      link_el.href = url;
      link_el.download = `easypassing-${type}-${a.referral_code}.pdf`;
      document.body.appendChild(link_el); link_el.click(); link_el.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Flyer downloaded');
    } catch (e) { toast.error(apiError(e)); }
    finally { setDl(null); }
  };

  const emailFlyer = async (type) => {
    setEm(type);
    try {
      const r = await ambAxios().post(`/ambassador/flyer/${type}/email`);
      toast.success(`Flyer emailed to ${r.data.sent_to || 'your inbox'}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setEm(null); }
  };

  const sendShare = async () => {
    if (!share?.email) { toast.error('Enter a recipient email'); return; }
    setSending(true);
    try {
      const r = await ambAxios().post(`/ambassador/flyer/${share.type}/email`, { recipient_email: share.email, note: share.note || undefined });
      toast.success(`Flyer sent to ${r.data.sent_to}`);
      setShare(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const flyers = [
    ['general', 'General flyer', 'Warm, all-purpose flyer for any audience.'],
    ['church', 'Church / community announcement', 'For faith communities and local groups.'],
    ['funeral_home', 'Funeral home handout', 'For families you serve.'],
    ['estate', 'Estate planning office handout', 'A companion to your clients\u2019 estate plan.'],
  ];

  const templates = [
    ['Social media caption', `💛 Planning ahead is one of the most loving gifts you can give your family. I'm proud to partner with EasyPassing — a secure place to store your important documents, record messages for loved ones, and organize your legacy. Get started free with my link: ${link}`],
    ['Short post / story', `Have you organized your family's "what if" plan? EasyPassing makes it simple and secure. Start free 👉 ${link}`],
    ['Email template', `Hi [Name],\n\nI wanted to share something meaningful with you. EasyPassing helps families organize their important documents, final wishes, and heartfelt messages — all in one secure place.\n\nYou can start for free here: ${link}\n\nIt's a simple step that brings real peace of mind.\n\nWarmly,\n${name}`],
    ['Text message', `Hi! I'm partnering with EasyPassing to help families protect what matters most. You can start free here: ${link}`],
    ['Church / community announcement', `Our community is partnering with EasyPassing to help families prepare for the future with dignity and peace of mind. EasyPassing lets you securely store documents, leave messages for loved ones, and organize your legacy. Learn more and start free: ${link}`],
    ['Estate planning office handout', `Recommend EasyPassing to your clients as a companion to their estate plan. It gives families a secure home for documents, final wishes, and personal messages. Client sign-up: ${link}`],
    ['Funeral home handout', `EasyPassing helps families organize their wishes and important information ahead of time — easing the burden on loved ones. Share with the families you serve: ${link}`],
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA] overflow-x-hidden" data-testid="ambassador-resources">
      <Header navigate={navigate} active="resources" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 flex flex-col sm:flex-row items-center gap-4">
          <div className="text-center sm:text-left flex-1">
            <p className="font-semibold" style={{ color: C.ink }}>Your referral link & code</p>
            <p className="text-sm mt-1" style={{ color: '#4A5560' }}>Code <b>{a.referral_code}</b></p>
            <p className="text-sm break-all" style={{ color: C.navy2 }}>{link}</p>
            <div className="flex gap-2 mt-2 justify-center sm:justify-start flex-wrap">
              <button onClick={() => copy(link)} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Copy className="h-3.5 w-3.5" /> Copy link</button>
              <button onClick={shareLink} className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1" style={{ background: C.navy }} data-testid="amb-share-link"><Share2 className="h-3.5 w-3.5" /> Share link</button>
              {a.qr_code_url && <a href={a.qr_code_url} download={`qr-${a.referral_code}.png`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Download className="h-3.5 w-3.5" /> QR flyer</a>}
            </div>
          </div>
          {a.qr_code_url && <img src={a.qr_code_url} alt="QR" className="w-24 h-24 rounded-lg" />}
        </div>
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5" data-testid="amb-flyers">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-5 w-5" style={{ color: C.navy2 }} />
            <p className="font-semibold" style={{ color: C.ink }}>Print-ready marketing flyers</p>
          </div>
          <p className="text-sm mb-4" style={{ color: '#4A5560' }}>Polished US Letter PDFs, personalized with your name, referral code, link & QR code. Print or share digitally.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {flyers.map(([type, title, desc]) => (
              <div key={type} className="border border-[#E8EAEE] rounded-xl p-4 flex flex-col">
                <p className="font-semibold text-sm" style={{ color: C.ink }}>{title}</p>
                <p className="text-xs mt-1 flex-1" style={{ color: C.mute }}>{desc}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => downloadFlyer(type)} disabled={dl === type}
                    className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1.5 disabled:opacity-60"
                    style={{ background: C.navy }} data-testid={`amb-flyer-${type}`}>
                    {dl === type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF
                  </button>
                  <button onClick={() => emailFlyer(type)} disabled={em === type}
                    className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 disabled:opacity-60"
                    style={{ borderColor: C.line, color: C.navy }} data-testid={`amb-flyer-email-${type}`}>
                    {em === type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email to me
                  </button>
                  <button onClick={() => setShare({ type, email: '', note: '' })}
                    className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5"
                    style={{ borderColor: C.line, color: C.navy }} data-testid={`amb-flyer-share-${type}`}>
                    <Send className="h-3.5 w-3.5" /> Share
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {['individuals', 'companies'].map((cat) => {
          const items = materials.filter((m) => m.category === cat);
          if (!items.length) return null;
          const heading = cat === 'individuals' ? 'Information for Individuals & Families' : 'Information for Companies & Partners';
          const sub = cat === 'individuals' ? 'Share these presentation pages with families exploring EasyPassing.' : 'Materials for companies, funeral homes, estate offices & partner groups.';
          return (
            <div key={cat} className="bg-white rounded-2xl border border-[#E8EAEE] p-5" data-testid={`amb-materials-${cat}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold" style={{ color: C.ink }}>{heading}</p>
                  <p className="text-sm mb-4" style={{ color: '#4A5560' }}>{sub}</p>
                </div>
                <button onClick={() => downloadPresentation(cat)} disabled={pdfCat === cat}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1.5 disabled:opacity-60"
                  style={{ background: C.navy }} data-testid={`materials-pdf-${cat}`}>
                  {pdfCat === cat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Full PDF
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {items.map((m) => (
                  <button key={m.material_id} onClick={() => setViewer(m)} className="group text-left" data-testid={`material-${m.material_id}`}>
                    <div className="aspect-[3/4] rounded-xl overflow-hidden border border-[#E8EAEE] bg-[#F7F8FA]">
                      <img src={m.image_url} alt={m.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
                    </div>
                    <p className="text-xs mt-1.5 truncate" style={{ color: C.mute }}>{m.title}</p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {templates.map(([title, body]) => (
          <div key={title} className="bg-white rounded-2xl border border-[#E8EAEE] p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold" style={{ color: C.ink }}>{title}</p>
              <button onClick={() => copy(body)} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Copy className="h-3.5 w-3.5" /> Copy</button>
            </div>
            <p className="text-sm whitespace-pre-line" style={{ color: '#4A5560' }}>{body}</p>
          </div>
        ))}
      </div>
      <AmbFooter />

      {viewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-8" onClick={() => setViewer(null)} data-testid="material-viewer">
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-white font-semibold">{viewer.title}</p>
              <div className="flex items-center gap-2">
                <a href={viewer.image_url} download target="_blank" rel="noreferrer" className="text-white/90 text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10"><Download className="h-4 w-4" /> Download</a>
                <button onClick={() => setViewer(null)} className="text-white/90 p-1.5 rounded-full bg-white/10" data-testid="material-viewer-close"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <img src={viewer.image_url} alt={viewer.title} className="w-full rounded-xl max-h-[80vh] object-contain bg-white" />
          </div>
        </div>
      )}

      {share && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="amb-share-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-lg" style={{ color: C.ink }}>Share this flyer</p>
              <button onClick={() => setShare(null)} data-testid="amb-share-close"><X className="h-5 w-5" style={{ color: C.mute }} /></button>
            </div>
            <p className="text-sm" style={{ color: '#4A5560' }}>We'll email the PDF, personalized with your referral code, to your friend.</p>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Recipient email</label>
              <input type="email" value={share.email} onChange={(e) => setShare({ ...share, email: e.target.value })}
                placeholder="friend@example.com" className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" data-testid="amb-share-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Personal note (optional)</label>
              <textarea value={share.note} onChange={(e) => setShare({ ...share, note: e.target.value })} rows={3}
                placeholder="Thought this might help your family…" className="w-full px-3 py-2 rounded-xl border border-[#E3E1DC] text-sm outline-none resize-none" data-testid="amb-share-note" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShare(null)} className="h-11 px-5 rounded-xl border font-semibold text-sm" style={{ borderColor: C.line, color: C.ink }}>Cancel</button>
              <button onClick={sendShare} disabled={sending} className="h-11 px-6 rounded-xl text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="amb-share-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send flyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Header({ navigate, active }) {
  const links = [['dashboard', 'Dashboard', LayoutDashboard], ['resources', 'Resources', Download], ['settings', 'Settings', SettingsIcon]];
  return (
    <header className="bg-white border-b border-[#E8EAEE] sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-1 overflow-x-auto no-scrollbar">
        <button onClick={() => navigate('/ambassador/dashboard')} className="mr-2 shrink-0"><ArrowLeft className="h-5 w-5" style={{ color: C.mute }} /></button>
        {links.map(([id, label, Icon]) => (
          <button key={id} onClick={() => navigate(`/ambassador/${id}`)} data-testid={`amb-nav-${id}`}
            className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium shrink-0 ${active === id ? 'text-white' : ''}`}
            style={active === id ? { background: C.navy } : { color: C.ink }}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>
    </header>
  );
}

export function AmbFooter() {
  return (
    <footer className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center" data-testid="amb-footer">
      <p className="text-xs" style={{ color: C.mute }}>
        <a href="/ambassador/terms" target="_blank" rel="noreferrer" className="hover:underline font-medium" style={{ color: C.navy2 }} data-testid="amb-footer-terms">Ambassador Terms &amp; Commission Policy</a>
        <span className="mx-2">·</span>
        <a href="/terms" target="_blank" rel="noreferrer" className="hover:underline" style={{ color: C.mute }}>Terms</a>
        <span className="mx-2">·</span>
        <a href="/privacy" target="_blank" rel="noreferrer" className="hover:underline" style={{ color: C.mute }}>Privacy</a>
      </p>
    </footer>
  );
}
